/**
 * Geocoding API Cost Monitoring Dashboard
 * 
 * Provides comprehensive monitoring, reporting, and analysis tools
 * to track cache effectiveness and API cost reduction.
 */

import GeocodingCache from './geocodingCache';

interface MonitoringConfig {
  enableRealTimeLogging: boolean;
  enablePerformanceTracking: boolean;
  enableCostEstimation: boolean;
}

class GeocodingMonitor {
  private config: MonitoringConfig;
  private startTime: number;
  private realTimeLog: any[] = [];

  constructor(config: Partial<MonitoringConfig> = {}) {
    this.config = {
      enableRealTimeLogging: true,
      enablePerformanceTracking: true,
      enableCostEstimation: true,
      ...config
    };
    this.startTime = Date.now();
  }

  /**
   * Generate a comprehensive monitoring report
   */
  generateComprehensiveReport() {
    const report = GeocodingCache.generateReport();
    const performance = this.getPerformanceMetrics();
    const costAnalysis = this.getCostAnalysis();
    
    console.group('🔍 Geocoding API Cost Monitoring Report');
    
    // Summary Section
    console.group('📊 Executive Summary');
    console.log('Session Duration:', `${report.summary.sessionDuration} minutes`);
    console.log('Total Requests:', report.summary.totalRequests);
    console.log('Cache Hit Rate:', report.summary.overallHitRate);
    console.log('Estimated Cost Savings:', report.summary.estimatedCostSavings);
    console.log('Cost Reduction:', `${this.calculateCostReduction(report)}%`);
    console.groupEnd();

    // Cache Performance
    console.group('🎯 Cache Performance Breakdown');
    Object.keys(report.breakdown).forEach(category => {
      const stats = report.breakdown[category];
      console.group(`${this.getCategoryIcon(category)} ${category.toUpperCase()}`);
      console.log(`Hit Rate: ${stats.hitRate}%`);
      console.log(`Total Requests: ${stats.totalRequests}`);
      console.log(`Cache Hits: ${stats.cacheHits} (Session: ${stats.sessionCacheHits || 0}, Cookie: ${stats.cookieCacheHits || 0})`);
      console.log(`API Calls: ${stats.apiCalls}`);
      console.log(`Errors: ${stats.errors} (${stats.errorRate}%)`);
      console.log(`Cost Savings: $${stats.costSavings.toFixed(4)}`);
      console.groupEnd();
    });
    console.groupEnd();

    // Cost Analysis
    console.group('💰 Cost Analysis');
    console.table(costAnalysis);
    console.groupEnd();

    // Performance Metrics
    if (this.config.enablePerformanceTracking) {
      console.group('⚡ Performance Metrics');
      console.table(performance);
      console.groupEnd();
    }

    // Recommendations
    // console.group('💡 Optimization Recommendations');
    // report.recommendations.forEach((rec, index) => {
    //   console.log(`${index + 1}. ${rec}`);
    // });
    
    // // Add custom recommendations
    // this.getCustomRecommendations(report).forEach((rec, index) => {
    //   console.log(`${report.recommendations.length + index + 1}. ${rec}`);
    // });
    // console.groupEnd();

    // Cache Contents Preview
    console.group('🗄️ Cache Contents Preview');
    this.displayCacheContents();
    console.groupEnd();

    console.groupEnd();

    return {
      ...report,
      performance,
      costAnalysis,
      customRecommendations: this.getCustomRecommendations(report)
    };
  }

  /**
   * Get performance metrics
   */
  private getPerformanceMetrics() {
    const sessionCache = JSON.parse(sessionStorage.getItem(GeocodingCache.GEOCODING_CACHE_KEY) || '{}');
    const cookieCache = this.getCookieCache();
    
    return {
      'Session Cache Size': `${Object.keys(sessionCache).length} entries`,
      'Cookie Cache Size': `${Object.keys(cookieCache).length} entries`,
      'Session Storage Used': `${this.formatBytes(JSON.stringify(sessionCache).length)}`,
      'Cookie Storage Used': `${this.formatBytes(JSON.stringify(cookieCache).length)}`,
      'Cache Efficiency': this.calculateCacheEfficiency(),
      'Average Response Time': 'N/A (requires instrumentation)',
      'Memory Usage': 'N/A (requires instrumentation)'
    };
  }

  /**
   * Get cost analysis
   */
  private getCostAnalysis() {
    const stats = GeocodingCache.getStats();
    const geocodingCost = 0.005; // $0.005 per request
    const autocompleteCost = 0.003; // $0.003 per session
    const placesCost = 0.017; // $0.017 per request
    
    return {
      'Geocoding API': {
        'Requests Made': stats.geocoding.apiCalls,
        'Requests Saved': stats.geocoding.cacheHits,
        'Cost Incurred': `$${(stats.geocoding.apiCalls * geocodingCost).toFixed(4)}`,
        'Cost Saved': `$${(stats.geocoding.cacheHits * geocodingCost).toFixed(4)}`,
        'Potential Total Cost': `$${((stats.geocoding.apiCalls + stats.geocoding.cacheHits) * geocodingCost).toFixed(4)}`
      },
      'Autocomplete API': {
        'Requests Made': stats.autocomplete.apiCalls,
        'Requests Saved': stats.autocomplete.cacheHits,
        'Cost Incurred': `$${(stats.autocomplete.apiCalls * autocompleteCost).toFixed(4)}`,
        'Cost Saved': `$${(stats.autocomplete.cacheHits * autocompleteCost).toFixed(4)}`,
        'Potential Total Cost': `$${((stats.autocomplete.apiCalls + stats.autocomplete.cacheHits) * autocompleteCost).toFixed(4)}`
      },
      'Places API': {
        'Requests Made': stats.places.apiCalls,
        'Requests Saved': stats.places.cacheHits,
        'Cost Incurred': `$${(stats.places.apiCalls * placesCost).toFixed(4)}`,
        'Cost Saved': `$${(stats.places.cacheHits * placesCost).toFixed(4)}`,
        'Potential Total Cost': `$${((stats.places.apiCalls + stats.places.cacheHits) * placesCost).toFixed(4)}`
      }
    };
  }

  /**
   * Calculate overall cost reduction percentage
   */
  private calculateCostReduction(report: any): string {
    const totalRequests = report.summary.totalRequests;
    const totalCacheHits = report.summary.totalCacheHits;
    
    if (totalRequests === 0) return '0.00';
    
    const reductionRate = (totalCacheHits / totalRequests) * 100;
    return reductionRate.toFixed(2);
  }

  /**
   * Get custom recommendations based on usage patterns
   */
  private getCustomRecommendations(report: any): string[] {
    const recommendations: string[] = [];
    const stats = report.breakdown;
    
    // Check session vs cookie cache usage
    if (stats.geocoding.sessionCacheHits > stats.geocoding.cookieCacheHits * 3) {
      recommendations.push('Consider increasing cookie cache size for better cross-tab performance');
    }
    
    // Check error rates
    Object.keys(stats).forEach(category => {
      const errorRate = parseFloat(stats[category].errorRate);
      if (errorRate > 5) {
        recommendations.push(`${category}: High error rate detected - check API key and network connectivity`);
      }
    });
    
    // Check if autocomplete is being used effectively
    if (stats.autocomplete.totalRequests > stats.geocoding.totalRequests * 2) {
      recommendations.push('High autocomplete usage detected - consider implementing query debouncing');
    }
    
    // Overall performance assessment
    const overallHitRate = parseFloat(report.summary.overallHitRate.replace('%', ''));
    if (overallHitRate > 90) {
      recommendations.push('Excellent cache performance! Consider documenting current settings as best practice');
    } else if (overallHitRate < 50) {
      recommendations.push('Cache hit rate below 50% - review cache configuration and user behavior patterns');
    }
    
    return recommendations;
  }

  /**
   * Display cache contents preview
   */
  private displayCacheContents() {
    const sessionCache = JSON.parse(sessionStorage.getItem(GeocodingCache.GEOCODING_CACHE_KEY) || '{}');
    const cookieCache = this.getCookieCache();
    
    console.log('Session Cache Entries (latest 5):');
    const sessionEntries = Object.entries(sessionCache)
      .sort(([,a]: any, [,b]: any) => (b.timestamp || 0) - (a.timestamp || 0))
      .slice(0, 5);
    
    sessionEntries.forEach(([key, value]: any) => {
      console.log(`  "${key}" → ${value.formattedAddress}`);
    });
    
    console.log('\nCookie Cache Entries (latest 5):');
    const cookieEntries = Object.entries(cookieCache)
      .sort(([,a]: any, [,b]: any) => (b.ts || 0) - (a.ts || 0))
      .slice(0, 5);
    
    cookieEntries.forEach(([key, value]: any) => {
      console.log(`  "${key}" → ${value.addr}`);
    });
  }

  /**
   * Helper methods
   */
  private getCategoryIcon(category: string): string {
    const icons = {
      geocoding: '🌍',
      autocomplete: '⌨️',
      places: '📍'
    };
    return icons[category] || '📊';
  }

  private getCookieCache() {
    try {
      const cookies = document.cookie.split(';');
      const targetCookie = cookies.find(cookie => 
        cookie.trim().startsWith(`${GeocodingCache.GEOCODING_COOKIE_KEY}=`)
      );
      
      if (targetCookie) {
        const cookieValue = targetCookie.split('=')[1];
        return JSON.parse(decodeURIComponent(cookieValue));
      }
    } catch (error) {
      console.warn('Failed to read cookie cache');
    }
    return {};
  }

  private formatBytes(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  private calculateCacheEfficiency(): string {
    const stats = GeocodingCache.getStats();
    const totalRequests = Object.values(stats).reduce((sum: number, cat: any) => sum + cat.totalRequests, 0);
    const totalHits = Object.values(stats).reduce((sum: number, cat: any) => sum + cat.cacheHits, 0);
    
    if (totalRequests === 0) return '0%';
    return `${((totalHits / totalRequests) * 100).toFixed(1)}%`;
  }

  /**
   * Real-time monitoring methods
   */
  startRealTimeMonitoring() {
    if (!this.config.enableRealTimeLogging) return;
    
    console.log('🔄 Real-time geocoding monitoring started');
    
    // Hook into the cache system for real-time logging
    const originalTrackStats = GeocodingCache.trackStats;
    GeocodingCache.trackStats = (type: string, source: string | null, category: string) => {
      originalTrackStats(type, source, category);
      
      this.realTimeLog.push({
        timestamp: new Date().toISOString(),
        type,
        source,
        category,
        sessionTime: Date.now() - this.startTime
      });
      
      // Keep only last 100 entries
      if (this.realTimeLog.length > 100) {
        this.realTimeLog.shift();
      }
      
      console.log(`📡 [${category}] ${type}${source ? ` from ${source}` : ''}`);
    };
  }

  stopRealTimeMonitoring() {
    console.log('⏹️ Real-time monitoring stopped');
    console.log('Real-time log entries:', this.realTimeLog.length);
  }

  /**
   * Export data for external analysis
   */
  exportData() {
    const report = this.generateComprehensiveReport();
    const exportData = {
      timestamp: new Date().toISOString(),
      report,
      realTimeLog: this.realTimeLog,
      sessionCache: JSON.parse(sessionStorage.getItem(GeocodingCache.GEOCODING_CACHE_KEY) || '{}'),
      cookieCache: this.getCookieCache()
    };
    
    console.log('📋 Copying monitoring data to clipboard...');
    
    const dataString = JSON.stringify(exportData, null, 2);
    
    // Try to copy to clipboard
    if (navigator.clipboard) {
      navigator.clipboard.writeText(dataString).then(() => {
        console.log('✅ Monitoring data copied to clipboard');
      }).catch(() => {
        console.log('❌ Failed to copy to clipboard, data logged below:');
        console.log(dataString);
      });
    } else {
      console.log('❌ Clipboard not available, data logged below:');
      console.log(dataString);
    }
    
    return exportData;
  }
}

// Create global instance for console access
const geocodingMonitor = new GeocodingMonitor();

// Make available globally for console testing
if (typeof window !== 'undefined') {
  (window as any).geocodingMonitor = geocodingMonitor;
  
  console.log(`
🔍 Geocoding API Cost Monitor Ready!

Use these commands in the browser console:

// Generate comprehensive report
geocodingMonitor.generateComprehensiveReport()

// Start real-time monitoring
geocodingMonitor.startRealTimeMonitoring()

// Stop real-time monitoring
geocodingMonitor.stopRealTimeMonitoring()

// Export all data for analysis
geocodingMonitor.exportData()

// Quick stats check
GeocodingCache.getStats()

// Reset statistics
GeocodingCache.resetStats()
  `);
}

export default GeocodingMonitor;
export { geocodingMonitor };
